import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { usePlatformFeatureFlags } from '@components/utils/DashboardFeatureFlags/usePlatformFeatureFlags'
import { useDashboardPermissions } from '@components/utils/DashboardPermissions'
import { Banner, Button, ModalBox } from '@fourthwall/components'
import { Offer } from '@fourthwall/services/dist/models/offer'
import { useAdminModeContext } from '@modules/App/providers/AdminModeProvider'
import { useCreateOfferMutation } from '@mutations'
import { reverse, routing } from '@utils/routing'

import { SHIPPING_MODAL_HASH } from '../../Product.consts'
import * as S from './CreateSelfOwnedProductModal.styled'
import { ManufacturingServiceSelect } from './ManufacturingServiceSelect'
import type { Props } from './types'

import ManufacturingService = Offer.ManufacturingService

export const CreateSelfOwnedProductModal = ({ close }: Props) => {
  const [selectedSelfOwnedManufacturingService, setSelectedSelfOwnedManufacturingService] =
    useState<ManufacturingService>()
  const navigate = useNavigate()
  const { hasFeatureFlag } = usePlatformFeatureFlags()
  const { hasPermission } = useDashboardPermissions()
  const adminMode = useAdminModeContext()

  const { createOfferMutation } = useCreateOfferMutation()

  const createSelfOwnedProduct = (manufacturingService?: ManufacturingService) => {
    if (!manufacturingService) {
      return
    }

    const withShippingChoiceModalOpen =
      ((hasFeatureFlag('assisted_product_shipping') &&
        hasPermission('products.thirdPartyLogistics.edit')) ||
        Boolean(adminMode?.isActive)) &&
      manufacturingService === 'MANUFACTURED_BY_CREATOR'
        ? SHIPPING_MODAL_HASH
        : ''

    return createOfferMutation.mutate(
      [
        {
          name: 'New Product',
          description: '',
          price: { value: 30, currency: 'USD' },
          weight: { value: 10, unit: 'oz' },
          manufacturingService,
          settings: { requiresShipping: true },
        },
      ],
      {
        onSuccess: ({ id }) => {
          close()
          navigate(
            `${reverse(routing.products.all.product.self, { productId: id })}${location.search}${withShippingChoiceModalOpen ? SHIPPING_MODAL_HASH : ''}`,
          )
        },
      },
    )
  }

  return (
    <ModalBox
      onClose={close}
      buttons={[
        <Button
          label="Create"
          appearance="primary"
          onClick={() => createSelfOwnedProduct(selectedSelfOwnedManufacturingService)}
          disabled={!selectedSelfOwnedManufacturingService}
          loading={createOfferMutation.isLoading}
        />,
      ]}
      size="small"
    >
      <ManufacturingServiceSelect
        selectedManufacturingService={selectedSelfOwnedManufacturingService}
        onSelect={setSelectedSelfOwnedManufacturingService}
      />
      <S.InfoContainer>
        <Banner appearance="warning">
          This view is only visible for Fourthwall admins. "Creator Manufactured" will always be
          selected when a creator chooses this option.
        </Banner>
      </S.InfoContainer>
    </ModalBox>
  )
}
